import React, { useState } from 'react';

import gql from 'graphql-tag';

import { useQuery } from '@apollo/client';

import { LaneType } from 'common-types';
import { getAdminClient } from 'lane-shared/apollo';
import { fromNow, longDateTimeZone } from 'lane-shared/helpers/formatters';

import IconButton from 'components/general/IconButton';
import { H3 } from 'components/typography';

import { PortalManagementUserEditType } from '../types';

import styles from './PortalManagementUserDeviceTokens.scss';

const updateUserMutation = gql`
  mutation updateUserForDeviceTokens($update: UserInput!) {
    updateUser(user: $update) {
      _id
    }
  }
`;

const userQuery = gql`
  query getUserDeviceTokens($id: UUID!) {
    userAdmin(_id: $id) {
      _id
      deviceTokens {
        _id
        _created
        _updated
        session {
          _id
        }
        whitelabel {
          _id
        }
        type
        token
      }
    }
  }
`;

export default function PortalManagementUserDeviceTokens({
  user,
}: {
  user: PortalManagementUserEditType;
}) {
  const [loading, setLoading] = useState(false);

  const { data } = useQuery<{
    userAdmin: {
      _id: LaneType.UUID;
      deviceTokens: {
        _id: LaneType.UUID;
        _created: LaneType.DateTime;
        _updated: LaneType.DateTime;
        session: {
          _id: LaneType.UUID;
        };
        whitelabel: {
          _id: LaneType.UUID;
        };
        type: 'apns' | 'gcm';
        token: string;
      }[];
    };
  }>(userQuery, {
    client: getAdminClient(),
    fetchPolicy: 'network-only',
    skip: !user._id,
    variables: {
      id: user._id,
    },
  });

  const deviceTokens = data?.userAdmin?.deviceTokens || [];

  async function deleteDeviceToken(_id: LaneType.UUID) {
    try {
      await window.Alert.confirm({
        title: 'Delete User Device Token.',
        message:
          'Are you sure you want to delete this user device token? This cannot be undone.',
        confirmText: 'Yes, delete',
      });
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // user cancelled
      return;
    }

    try {
      setLoading(true);
      const deviceToken = deviceTokens.find(token => token._id === _id);

      await getAdminClient().mutate({
        mutation: updateUserMutation,
        refetchQueries: ['getUserDeviceTokens'],
        variables: {
          update: {
            _id: user._id,
            deviceTokens: [
              {
                _pull: true,
                _id,
                type: deviceToken?.type,
                token: deviceToken?.token,
              },
            ],
          },
        },
      });
    } catch (err) {
      window.Alert.alert({
        title: 'Failed',
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.PortalManagementUserDeviceTokens}>
      <H3>Device Tokens</H3>
      <table className={styles.deviceTokens}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Session ID</th>
            <th>Whitelabel ID</th>
            <th>Type</th>
            <th>Token</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {deviceTokens.map(deviceToken => (
            <tr key={deviceToken._id}>
              <td>{deviceToken._id}</td>
              <td>{longDateTimeZone(deviceToken._created)}</td>
              <td>{fromNow(deviceToken._updated)}</td>
              <td>{deviceToken.session?._id}</td>
              <td>{deviceToken.whitelabel?._id}</td>
              <td>{deviceToken.type}</td>
              <td>{deviceToken.token}</td>
              <td>
                <IconButton
                  loading={loading}
                  icon="trash"
                  onClick={() => deleteDeviceToken(deviceToken._id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
